import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { colors } from '../../styles/GlobalStyles';
import { classService } from '../../services/classService';
import { Button } from '../../components/Button';
import { Modal } from '../../components/Modal';
import { Input, Label, FormGroup, Select, TextArea } from '../../components/Input';
import { Loader } from '../../components/Loader';
import { CalendarView } from '../../components/CalendarView';
import { toast } from 'react-toastify';

const Container = styled.div``;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  flex-wrap: wrap;
  gap: 15px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 20px;
  }
`;

const Title = styled.h1`
  color: ${colors.primary};
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
`;

const Card = styled.div`
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px ${colors.shadow};
  transition: transform 0.3s ease;

  &:hover {
    transform: translateY(-4px);
  }
`;

const ClassTitle = styled.h3`
  color: ${colors.primary};
  margin-bottom: 10px;
`;

const ClassInfo = styled.div`
  color: ${colors.text};
  margin-bottom: 8px;
  font-size: 14px;
`;

const LocationLink = styled.a`
  color: ${colors.primary};
  text-decoration: none;
  cursor: pointer;
  transition: color 0.3s ease;

  &:hover {
    color: ${colors.primaryDark};
    text-decoration: underline;
  }
`;

const ClassDescription = styled.p`
  color: ${colors.textLight};
  font-size: 14px;
  margin-bottom: 15px;
`;

const Progress = styled.div`
  width: 100%;
  height: 8px;
  background-color: ${colors.secondary};
  border-radius: 4px;
  overflow: hidden;
  margin: 10px 0;
`;

const ProgressBar = styled.div`
  height: 100%;
  background-color: ${props => {
    if (props.percentage >= 100) return colors.error;
    if (props.percentage >= 80) return colors.warning;
    return colors.success;
  }};
  width: ${props => props.percentage}%;
  transition: width 0.3s ease;
`;

const CapacityText = styled.div`
  font-size: 12px;
  color: ${colors.textLight};
  margin-top: 5px;
`;

const DaySection = styled.div`
  margin-bottom: 30px;
`;

const DayHeader = styled.div`
  background: ${colors.primary};
  color: white;
  padding: 15px 20px;
  border-radius: 8px 8px 0 0;
  font-size: 18px;
  font-weight: bold;
  margin-bottom: 0;
`;

const DayGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
  background: white;
  padding: 20px;
  border-radius: 0 0 8px 8px;
  box-shadow: 0 2px 8px ${colors.shadow};
`;

const Actions = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 15px;
`;

const Badge = styled.span`
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  background-color: ${props => props.recurring ? colors.success : colors.secondary};
  color: ${props => props.recurring ? 'white' : colors.text};
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 20px;
`;

const RecurringOptions = styled.div`
  display: ${props => props.show ? 'block' : 'none'};
`;

const ViewToggle = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    justify-content: center;
  }
`;

const ToggleButton = styled.button`
  padding: 8px 16px;
  border: 2px solid ${props => props.active ? colors.primary : colors.border};
  background: ${props => props.active ? colors.primary : 'white'};
  color: ${props => props.active ? 'white' : colors.text};
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.3s ease;

  &:hover {
    border-color: ${colors.primary};
    background: ${props => props.active ? colors.primary : colors.secondary};
  }
`;

const weekDays = [
  { value: 0, label: 'Pazar' },
  { value: 1, label: 'Pazartesi' },
  { value: 2, label: 'Salı' },
  { value: 3, label: 'Çarşamba' },
  { value: 4, label: 'Perşembe' },
  { value: 5, label: 'Cuma' },
  { value: 6, label: 'Cumartesi' }
];

export const AdminClasses = () => {
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedClass, setSelectedClass] = useState(null);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'calendar'
  const [currentWeekStart, setCurrentWeekStart] = useState(getStartOfWeek(new Date()));

  // Mobilde otomatik olarak liste görünümü kullan
  useEffect(() => {
    const checkMobile = () => {
      if (window.innerWidth <= 768) {
        setViewMode('grid');
      }
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    instructor: '',
    date: '',
    startTime: '',
    endTime: '',
    location: 'Mutlukent Mah. 1951. Sok. No:18, Ankara, Turkey 06800',
    isOnline: false,
    onlineLink: '',
    isFree: false,
    capacity: 10,
    isRecurring: false,
    recurringDay: 1,
    isActive: true
  });

  useEffect(() => {
    fetchClasses();
  }, [currentWeekStart, viewMode]);

  // Get start of week (Monday)
  function getStartOfWeek(date) {
    const day = date.getDay();
    const diff = day === 0 ? -6 : 1 - day;
    const monday = new Date(date);
    monday.setDate(date.getDate() + diff);
    monday.setHours(0, 0, 0, 0);
    return monday;
  }

  // Get classes for the current week (for grid view)
  const getWeekClasses = () => {
    const weekEnd = new Date(currentWeekStart);
    weekEnd.setDate(currentWeekStart.getDate() + 7);
    
    const expandedClasses = [];
    const now = new Date();
    const fiveHoursAgo = new Date(now.getTime() - (5 * 60 * 60 * 1000));
    
    classes.forEach(classItem => {
      if (classItem.isRecurring) {
        // For recurring classes, generate instances for each day in the week
        for (let i = 0; i < 7; i++) {
          const date = new Date(currentWeekStart);
          date.setDate(currentWeekStart.getDate() + i);
          
          const classDate = new Date(classItem.date + 'T00:00:00');
          
          // Only show if it's the same day of week and on or after the class start date
          if (date.getDay() === classDate.getDay() && date >= classDate) {
            const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
            
            // Check if class is not too old (5 hours after class time)
            const classDateTime = new Date(`${dateStr}T${classItem.startTime || classItem.time || '00:00'}`);
            if (classDateTime >= fiveHoursAgo) {
              // Use the occupancy data from weekOccupancy for this specific date
              const occupancyData = classItem.weekOccupancy?.[dateStr] || {
                currentBookings: 0,
                availableSpots: classItem.capacity,
                isFull: false
              };
              
              expandedClasses.push({
                ...classItem,
                date: dateStr,
                displayDate: date.toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric', weekday: 'long' }),
                currentBookings: occupancyData.currentBookings,
                availableSpots: occupancyData.availableSpots,
                isFull: occupancyData.isFull
              });
            }
          }
        }
      } else {
        // For non-recurring classes, check if it's in this week
        const classDate = new Date(classItem.date + 'T00:00:00');
        if (classDate >= currentWeekStart && classDate < weekEnd) {
          // Check if class is not too old (5 hours after class time)
          const classDateTime = new Date(`${classItem.date}T${classItem.startTime || classItem.time || '00:00'}`);
          if (classDateTime >= fiveHoursAgo) {
            // Use the occupancy data that was already fetched with the class
            expandedClasses.push({
              ...classItem,
              displayDate: classDate.toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric', weekday: 'long' }),
              currentBookings: classItem.currentBookings || 0,
              availableSpots: classItem.availableSpots || (classItem.capacity - (classItem.currentBookings || 0)),
              isFull: classItem.isFull || false
            });
          }
        }
      }
    });
    
    // Sort by date and time
    return expandedClasses.sort((a, b) => {
      const dateCompare = (a.date || '').localeCompare(b.date || '');
      if (dateCompare !== 0) return dateCompare;
      return (a.startTime || a.time || '').localeCompare(b.startTime || b.time || '');
    });
  };

  // Get classes grouped by day for daily view
  const getDailyClasses = () => {
    const weekClasses = getWeekClasses();
    const dailyGroups = {};
    
    weekClasses.forEach(classItem => {
      const date = classItem.date;
      if (!dailyGroups[date]) {
        dailyGroups[date] = [];
      }
      dailyGroups[date].push(classItem);
    });
    
    // Convert to array and sort by date
    return Object.entries(dailyGroups)
      .map(([date, classes]) => ({
        date,
        classes: classes.sort((a, b) => (a.startTime || a.time || '').localeCompare(b.startTime || b.time || ''))
      }))
      .sort((a, b) => a.date.localeCompare(b.date));
  };

  // Check if a class is in the past
  const isClassPast = (classDate, classTime) => {
    const now = new Date();
    const classDateTime = new Date(`${classDate}T${classTime || '00:00'}`);
    return classDateTime < now;
  };

  const goToPreviousWeek = () => {
    const newDate = new Date(currentWeekStart);
    newDate.setDate(currentWeekStart.getDate() - 7);
    setCurrentWeekStart(newDate);
  };

  const goToNextWeek = () => {
    const newDate = new Date(currentWeekStart);
    newDate.setDate(currentWeekStart.getDate() + 7);
    setCurrentWeekStart(newDate);
  };

  const goToToday = () => {
    setCurrentWeekStart(getStartOfWeek(new Date()));
  };

  const getWeekRange = () => {
    const weekEnd = new Date(currentWeekStart);
    weekEnd.setDate(currentWeekStart.getDate() + 6);

    const startDay = currentWeekStart.getDate();
    const endDay = weekEnd.getDate();

    if (currentWeekStart.getMonth() === weekEnd.getMonth()) {
      const month = currentWeekStart.toLocaleDateString('tr-TR', { month: 'long' });
      const year = currentWeekStart.getFullYear();
      return `${startDay} - ${endDay} ${month} ${year}`;
    } else {
      const startMonth = currentWeekStart.toLocaleDateString('tr-TR', { month: 'long' });
      const endMonth = weekEnd.toLocaleDateString('tr-TR', { month: 'long' });
      const year = currentWeekStart.getFullYear();
      return `${startDay} ${startMonth} - ${endDay} ${endMonth} ${year}`;
    }
  };

  const fetchClasses = async () => {
    try {
      setLoading(true);
      
      // Use getAllClasses to avoid cache issues
      const allClasses = await classService.getAllClasses();
      console.log('All classes:', allClasses);
      setClasses(allClasses);
      
    } catch (error) {
      toast.error('Dersler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (classItem = null) => {
    if (classItem) {
      setSelectedClass(classItem);
      setFormData({
        title: classItem.title,
        description: classItem.description,
        instructor: classItem.instructor,
        date: classItem.date,
        startTime: classItem.startTime || classItem.time || '',
        endTime: classItem.endTime || '',
        location: classItem.location || 'Mutlukent Mah. 1951. Sok. No:18, Ankara, Turkey 06800',
        isOnline: classItem.isOnline || false,
        onlineLink: classItem.onlineLink || '',
        isFree: classItem.isFree || false,
        capacity: classItem.capacity,
        isRecurring: classItem.isRecurring,
        recurringDay: classItem.recurringDay || 1,
        isActive: classItem.isActive
      });
    } else {
      setSelectedClass(null);
      setFormData({
        title: '',
        description: '',
        instructor: '',
        date: '',
        startTime: '',
        endTime: '',
        location: 'Mutlukent Mah. 1951. Sok. No:18, Ankara, Turkey 06800',
        isOnline: false,
        onlineLink: '',
        isFree: false,
        capacity: 10,
        isRecurring: false,
        recurringDay: 1,
        isActive: true
      });
    }
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedClass(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const submitFormData = new FormData();
      
      // Append all form fields
      Object.keys(formData).forEach(key => {
        submitFormData.append(key, formData[key]);
      });

      // Debug: Log the form data being sent
      console.log('Form data being sent:', {
        title: formData.title,
        date: formData.date,
        startTime: formData.startTime,
        selectedClassId: selectedClass?.id,
        allFormData: formData
      });

      if (selectedClass) {
        await classService.updateClass(selectedClass.id, submitFormData);
        toast.success('Ders güncellendi');
        
        // Force complete page refresh to ensure data is updated
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      } else {
        await classService.createClass(submitFormData);
        toast.success('Ders oluşturuldu');
        fetchClasses();
      }
      handleCloseModal();
    } catch (error) {
      toast.error(error.response?.data?.error || 'İşlem başarısız');
    }
  };

  const handleUpdateClass = async (id, formData) => {
    try {
      await classService.updateClass(id, formData);
      toast.success('Ders güncellendi');
      fetchClasses();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Güncelleme işlemi başarısız');
      throw error;
    }
  };

  const handleDeleteClass = async (id) => {
    try {
      await classService.deleteClass(id);
      toast.success('Ders silindi');
      fetchClasses();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Silme işlemi başarısız');
      throw error;
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Bu dersi silmek istediğinize emin misiniz?')) {
      await handleDeleteClass(id);
    }
  };

  if (loading) {
    return <Loader fullScreen />;
  }

  return (
    <Container>
      <Header>
        <Title>Ders Yönetimi</Title>
        <Button onClick={() => handleOpenModal()}>+ Yeni Ders Ekle</Button>
      </Header>

      <ViewToggle>
        <ToggleButton 
          active={viewMode === 'grid'} 
          onClick={() => setViewMode('grid')}
        >
          📋 Liste Görünümü
        </ToggleButton>
        <ToggleButton 
          active={viewMode === 'calendar'} 
          onClick={() => setViewMode('calendar')}
          style={{ display: 'none' }}
          className="desktop-only"
        >
          📅 Takvim Görünümü
        </ToggleButton>
      </ViewToggle>

      {viewMode === 'grid' ? (
        <>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            marginBottom: '20px', 
            background: 'white', 
            padding: '15px', 
            borderRadius: '8px', 
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            flexWrap: 'wrap',
            gap: '10px'
          }}>
            <Button onClick={goToPreviousWeek} size="small" style={{ minWidth: '80px', fontSize: '12px', padding: '8px 12px' }}>← Önceki</Button>
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              gap: '8px', 
              flex: '1',
              minWidth: '140px',
              maxWidth: '200px'
            }}>
              <h3 style={{ 
                margin: 0, 
                color: '#2C5F2D', 
                fontSize: '16px', 
                textAlign: 'center', 
                fontWeight: '600',
                lineHeight: '1.2'
              }}>
                {getWeekRange()}
              </h3>
              <Button onClick={goToToday} size="small" variant="secondary" style={{ fontSize: '12px', padding: '6px 12px', minWidth: '60px' }}>Bugün</Button>
            </div>
            <Button onClick={goToNextWeek} size="small" style={{ minWidth: '80px', fontSize: '12px', padding: '8px 12px' }}>Sonraki →</Button>
          </div>
          {getDailyClasses().map((dayData, dayIndex) => (
            <DaySection key={dayData.date}>
              <DayHeader>
                {new Date(dayData.date + 'T00:00:00').toLocaleDateString('tr-TR', { 
                  day: 'numeric', 
                  month: 'long', 
                  year: 'numeric', 
                  weekday: 'long' 
                })}
              </DayHeader>
              <DayGrid>
                {dayData.classes.map((classItem, index) => {
                  const percentage = (classItem.currentBookings / classItem.capacity) * 100;
                  const isPast = isClassPast(classItem.date, classItem.startTime || classItem.time);
                  
                  // Debug: Log percentage calculation
                  console.log('Percentage calculation:', {
                    title: classItem.title,
                    currentBookings: classItem.currentBookings,
                    capacity: classItem.capacity,
                    percentage: percentage
                  });
                  
                  return (
                    <Card key={`${classItem.id}-${classItem.date}`} style={{ opacity: isPast ? 0.6 : 1 }}>
                      <ClassTitle>{classItem.title}</ClassTitle>
                      <ClassDescription>{classItem.description}</ClassDescription>
                      
                      <ClassInfo>👨‍🏫 <strong>Eğitmen:</strong> {classItem.instructor}</ClassInfo>
                      
                      {classItem.isOnline ? (
                        <ClassInfo>
                          💻 <strong>Online Ders</strong>
                          {classItem.onlineLink && (
                            <>
                              {' - '}
                              <LocationLink 
                                href={classItem.onlineLink}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                Derse Katıl
                              </LocationLink>
                            </>
                          )}
                        </ClassInfo>
                      ) : classItem.location && (
                        <ClassInfo>
                          📍 <strong>Adres:</strong>{' '}
                          <LocationLink 
                            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(classItem.location)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {classItem.location}
                          </LocationLink>
                        </ClassInfo>
                      )}
                      <ClassInfo>🕐 <strong>Saat:</strong> {classItem.startTime} {classItem.endTime ? `- ${classItem.endTime}` : ''}</ClassInfo>
                      
                      {isPast && (
                        <Badge style={{ background: '#ff6b6b', color: 'white' }}>
                          ⏰ Geçti
                        </Badge>
                      )}
                      
                      <Badge recurring={classItem.isRecurring}>
                        {classItem.isRecurring ? '🔄 Tekrarlı' : '📌 Tek Seferlik'}
                      </Badge>

                      {/* Doluluk oranları gösterilmiyor - sadece ders düzenleme çalışsın */}
                      {/* <Progress>
                        <ProgressBar percentage={percentage} />
                      </Progress>
                      <CapacityText>
                        {classItem.currentBookings} / {classItem.capacity} Dolu 
                        ({percentage.toFixed(0)}%)
                      </CapacityText> */}

                      <Actions>
                        <Button size="small" onClick={() => handleOpenModal(classItem)}>
                          Düzenle
                        </Button>
                        <Button 
                          size="small" 
                          variant="danger" 
                          onClick={() => handleDelete(classItem.id)}
                        >
                          Sil
                        </Button>
                      </Actions>
                    </Card>
                  );
                })}
              </DayGrid>
            </DaySection>
          ))}

          {getDailyClasses().length === 0 && (
            <div style={{ textAlign: 'center', padding: '40px', background: 'white', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
              <p style={{ color: colors.textLight }}>
                Bu hafta için ders bulunmamaktadır.
              </p>
            </div>
          )}
        </>
      ) : (
        <CalendarView 
          classes={classes}
          onClassUpdate={handleUpdateClass}
          onClassDelete={handleDeleteClass}
        />
      )}

      <Modal
        isOpen={modalOpen}
        onClose={handleCloseModal}
        title={selectedClass ? 'Ders Düzenle' : 'Yeni Ders Ekle'}
        maxWidth="600px"
      >
        <form onSubmit={handleSubmit}>
          <FormGroup>
            <Label>Ders Başlığı</Label>
            <Input
              value={formData.title}
              onChange={(e) => setFormData({...formData, title: e.target.value})}
              required
              placeholder="örn: Sabah Yoga"
            />
          </FormGroup>

          <FormGroup>
            <Label>Açıklama (Opsiyonel)</Label>
            <TextArea
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              placeholder="Ders hakkında kısa bilgi..."
            />
          </FormGroup>

          <FormGroup>
            <Label>Eğitmen</Label>
            <Input
              value={formData.instructor}
              onChange={(e) => setFormData({...formData, instructor: e.target.value})}
              required
              placeholder="örn: Ahmet Yılmaz"
            />
          </FormGroup>

          <FormGroup>
            <Label>
              <input
                type="checkbox"
                checked={formData.isOnline}
                onChange={(e) => setFormData({...formData, isOnline: e.target.checked, location: e.target.checked ? '' : 'Mutlukent Mah. 1951. Sok. No:18, Ankara, Turkey 06800'})}
                style={{ marginRight: '8px' }}
              />
              Online Ders
            </Label>
          </FormGroup>

          <FormGroup>
            <Label>
              <input
                type="checkbox"
                checked={formData.isFree}
                onChange={(e) => setFormData({...formData, isFree: e.target.checked})}
                style={{ marginRight: '8px' }}
              />
              Ücretsiz Ders
            </Label>
          </FormGroup>

          {formData.isOnline ? (
            <FormGroup>
              <Label>Online Ders Linki (Opsiyonel)</Label>
              <Input
                value={formData.onlineLink}
                onChange={(e) => setFormData({...formData, onlineLink: e.target.value})}
                placeholder="örn: https://zoom.us/j/123456789"
              />
            </FormGroup>
          ) : (
            <FormGroup>
              <Label>Adres</Label>
              <Input
                value={formData.location}
                onChange={(e) => setFormData({...formData, location: e.target.value})}
                placeholder="örn: Seed Yoga Studio, İstanbul"
              />
            </FormGroup>
          )}

          <FormGroup>
            <Label>Tarih</Label>
            <Input
              type="date"
              value={formData.date}
              onChange={(e) => setFormData({...formData, date: e.target.value})}
              required
            />
          </FormGroup>

          <FormGroup>
            <Label>Başlangıç Saati</Label>
            <Input
              type="time"
              value={formData.startTime}
              onChange={(e) => setFormData({...formData, startTime: e.target.value})}
              required
            />
          </FormGroup>

          <FormGroup>
            <Label>Bitiş Saati (Opsiyonel)</Label>
            <Input
              type="time"
              value={formData.endTime}
              onChange={(e) => setFormData({...formData, endTime: e.target.value})}
            />
          </FormGroup>

          <FormGroup>
            <Label>Kontenjan</Label>
            <Input
              type="number"
              min="1"
              value={formData.capacity}
              onChange={(e) => setFormData({...formData, capacity: parseInt(e.target.value)})}
              required
            />
          </FormGroup>

          <FormGroup>
            <Label>
              <input
                type="checkbox"
                checked={formData.isRecurring}
                onChange={(e) => setFormData({...formData, isRecurring: e.target.checked})}
                style={{ marginRight: '8px' }}
              />
              Tekrarlı Ders (Her hafta aynı gün)
            </Label>
          </FormGroup>

          <RecurringOptions show={formData.isRecurring}>
            <FormGroup>
              <Label>Tekrar Günü</Label>
              <Select
                value={formData.recurringDay}
                onChange={(e) => setFormData({...formData, recurringDay: parseInt(e.target.value)})}
              >
                {weekDays.map(day => (
                  <option key={day.value} value={day.value}>{day.label}</option>
                ))}
              </Select>
            </FormGroup>
          </RecurringOptions>

          <FormGroup>
            <Label>Durum</Label>
            <Select
              value={formData.isActive}
              onChange={(e) => setFormData({...formData, isActive: e.target.value === 'true'})}
            >
              <option value="true">Aktif</option>
              <option value="false">Pasif</option>
            </Select>
          </FormGroup>

          <ButtonGroup>
            <Button type="submit" fullWidth>
              {selectedClass ? 'Güncelle' : 'Oluştur'}
            </Button>
            <Button type="button" variant="secondary" fullWidth onClick={handleCloseModal}>
              İptal
            </Button>
          </ButtonGroup>
        </form>
      </Modal>
    </Container>
  );
};


